import React from 'react';
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { BadgeVariant } from '@/types/common.types';

export interface AlertProps {
  variant?: BadgeVariant;
  title?: string;
  /** Whether the alert can be dismissed */
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
  children?: React.ReactNode;
}

const variantConfig: Record<
  BadgeVariant,
  { containerClass: string; iconClass: string; Icon: React.ElementType }
> = {
  success: {
    containerClass: 'bg-green-50 border-green-200 text-green-800',
    iconClass: 'text-green-500',
    Icon: CheckCircle2,
  },
  error: {
    containerClass: 'bg-red-50 border-red-200 text-red-800',
    iconClass: 'text-red-500',
    Icon: AlertCircle,
  },
  warning: {
    containerClass: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    iconClass: 'text-yellow-500',
    Icon: AlertTriangle,
  },
  info: {
    containerClass: 'bg-blue-50 border-blue-200 text-blue-800',
    iconClass: 'text-blue-500',
    Icon: Info,
  },
  neutral: {
    containerClass: 'bg-slate-50 border-slate-200 text-slate-700',
    iconClass: 'text-slate-500',
    Icon: Info,
  },
  purple: {
    containerClass: 'bg-purple-50 border-purple-200 text-purple-800',
    iconClass: 'text-purple-500',
    Icon: Info,
  },
};

export function Alert({
  variant = 'info',
  title,
  dismissible = false,
  onDismiss,
  className,
  children,
}: AlertProps) {
  const { containerClass, iconClass, Icon } = variantConfig[variant];

  return (
    <div
      role="alert"
      className={cn('flex gap-3 rounded-md border p-4', containerClass, className)}
    >
      <Icon className={cn('h-5 w-5 flex-shrink-0 mt-0.5', iconClass)} aria-hidden="true" />

      <div className="flex-1 min-w-0">
        {title && <p className="text-sm font-semibold mb-1">{title}</p>}
        {children && <div className="text-sm opacity-90">{children}</div>}
      </div>

      {dismissible && (
        <button
          type="button"
          onClick={onDismiss}
          className={cn(
            'flex-shrink-0 rounded-md p-0.5 -mr-0.5 -mt-0.5 opacity-60 hover:opacity-100 transition-opacity',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-blue-500',
          )}
          aria-label="Dismiss alert"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
